import { inject, Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl, ValidatorFn, Validators } from '@angular/forms';
import {
  CivilEngineeringEnum,
  ConnectionChangeDto,
  ConnectionChangeRequestTypeEnum
} from '@bo-client/shared/models/connectionChange.dto';
import {
  ORAdditionalInformationForm,
  ORConnectionChangeOperationsForm,
  ORContactDataForm,
  OrderRequestWizardModelForm,
  ORTechnicalDataForm
} from '@bo-client/shared/models/orderRequestWizard.model';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import { AnswerWithMoreInfoModel } from '@bo-client/activation-management/shared/models/AnswerWithMoreInfoModel';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { uri } from '@bo-client/shared/constants/uri';
import { ValidatorsService } from '@bo-client/shared/forms/validators.service';
import { DocumentDtoModel, UploadForm } from '@bo-client/shared/models/documentDto';
import { RequestExecuteActionDto } from '@bo-client/shared/models/requestExecuteAction.dto';
import { ArticleCase } from '@bo-client/shared/constants/articles';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ConnectionChangeRequestTypeConfig } from '@bo-client/shared/config.model';
import { minDateValidator, requireDocumentsValidator } from '@bo-client/shared/forms/forms.validators';
import { FieldConfigEnum } from '@bo-client/shared/constants/enums';
import { AllAreaEnum } from '@bo-client/activation-management/shared/models/commissioningModel';

@Injectable({
  providedIn: 'root'
})
export class OrderRequestWizardService {
  private formBuilder = inject(FormBuilder);
  private createFormsService = inject(CreateFormsService);
  private httpHapService = inject(HttpHapService);
  private validatorsService = inject(ValidatorsService);
  private dataShareService = inject(DataShareService);

  submitNewRequest(data: ConnectionChangeDto) {
    return this.httpHapService.postTyped<AnswerWithMoreInfoModel>(
      data,
      `${uri.hapExternalConnectionChangeRequestOffer}`
    );
  }

  submitFeedback(data: ConnectionChangeDto, token?: string) {
    const requestData: RequestExecuteActionDto<ConnectionChangeDto> = {
      id: data.id,
      token,
      subject: `Rückmeldung zu ${ConnectionChangeDto.GET_FORMAL_SUBJECT_NAME(
        data.connectionChangeRequestType,
        ArticleCase.dat
      )}`,
      comment: null,
      additionalData: data
    };
    return this.httpHapService.put(requestData, `${uri.hapExternalConnectionChangeRequest}`);
  }

  submitAcceptOffer(data: ConnectionChangeDto, token: string) {
    const requestData: RequestExecuteActionDto<string> = {
      id: data.id,
      token,
      subject: `Angebotsannahme zu ${ConnectionChangeDto.GET_FORMAL_SUBJECT_NAME(
        data.connectionChangeRequestType,
        ArticleCase.dat
      )}`,
      comment: null,
      additionalData: '' // wird nicht verwendet, aber vom backend erwartet
    };
    return this.httpHapService.post(requestData, `${uri.hapExternalConnectionChangeRequestConfirmOffer}`);
  }

  createStandardForm$(model?: ConnectionChangeDto): Observable<OrderRequestWizardModelForm> {
    const modelData =
      model || (new ConnectionChangeDto(ConnectionChangeRequestTypeEnum.CHANGE) as ConnectionChangeDto);
    return this.dataShareService
      .getSingleTenantConfig$<ConnectionChangeRequestTypeConfig[]>(
        'connectionChangeRequest.connectionChangeRequestTypes'
      )
      .pipe(
        map((config) => {
          return (
            ConnectionChangeRequestTypeConfig.getConnectionChangeRequestTypeConfig(
              modelData.area,
              modelData.connectionChangeRequestType,
              config
            ) || {}
          );
        }),
        map((config) => {
          return this.formBuilder.group({
            connectionObject: this.createFormsService.connectionObjectFormWithValidatorV2(
              modelData.connectionObject || {}
            ),
            connectionChangeOperations: this.createConnectionChangeOperationsForm(modelData, config),
            technicalData: this.createTechnicalDataForm(modelData, config),
            additionalInformation: this.createAdditionalInformationForm(modelData, config),
            contactData: this.createContactDataForm(modelData, config),
            acceptConditions: [false, this.validatorsService.checkboxRequiredValidator()]
          });
        })
      );
  }

  getEntityAsExternal(id: string | number, token: string) {
    return this.httpHapService.getTyped<{
      entity: ConnectionChangeDto;
      answerState: string;
      navigateTo: string;
      message: unknown;
    }>(`${uri.hapExternalConnectionChangeRequest}/${id}/${token}`);
  }

  private createConnectionChangeOperationsForm(
    model: ConnectionChangeDto,
    config: ConnectionChangeRequestTypeConfig
  ): ORConnectionChangeOperationsForm {
    return this.formBuilder.group({
      powerIncrease: [!!model?.powerIncrease],
      moveServiceEntranceBox: [!!model?.moveServiceEntranceBox],
      moveConnectionLine: [!!model?.moveConnectionLine]
    });
  }

  private createTechnicalDataForm(
    model: ConnectionChangeDto,
    config: ConnectionChangeRequestTypeConfig
  ): ORTechnicalDataForm {
    return this.formBuilder.group(
      {
        installedPower: [model?.installedPower || null, Validators.min(0)],
        installedFuse: [model?.installedFuse || null],
        requiredPower: [
          model?.requiredPower || null,
          [
            Validators.min(0),
            requireOnPowerIncreaseValidator,
            ...(model.connectionChangeRequestType === ConnectionChangeRequestTypeEnum.TEMPORARY &&
            config.requiredPowerFieldInTemporary === FieldConfigEnum.REQUIRED
              ? [Validators.required]
              : []),
            ...(!(model.area === AllAreaEnum.Water || model.area === AllAreaEnum.Heat)
              ? [minTechnicalRequiredPowerValidator]
              : [])
          ]
        ],
        civilEngineering: [
          model?.civilEngineering || null,
          [...(config.explosiveField === FieldConfigEnum.REQUIRED ? [requireOnPowerIncreaseValidator] : [])]
        ],
        isEmptyHouse: [!!model?.isEmptyHouse],
        hasNoMeter: [
          !!model &&
            model.connectionChangeRequestType != ConnectionChangeRequestTypeEnum.TEMPORARY &&
            !model.meterNumber
        ],
        meterNumber: [
          model?.meterNumber || null,
          [
            ...(config.deviceNumberField === FieldConfigEnum.REQUIRED &&
            model?.connectionChangeRequestType != ConnectionChangeRequestTypeEnum.TEMPORARY
              ? [requireMeterNumberValidator]
              : [])
          ]
        ],
        technicalComment: [model?.technicalComment || null],
        fromDate: [
          model?.fromDate ? new Date(model.fromDate) : null,
          [
            minDateValidator,
            ...(model?.connectionChangeRequestType === ConnectionChangeRequestTypeEnum.TEMPORARY
              ? [Validators.required]
              : [])
          ]
        ],
        toDate: [
          model?.toDate ? new Date(model.toDate) : null,
          [
            minDateValidator,
            ...(model?.connectionChangeRequestType === ConnectionChangeRequestTypeEnum.TEMPORARY
              ? [Validators.required]
              : [])
          ]
        ],
        sitePlanUploads: this.createUploadsFormArray(model.uploads, {
          tag: 'sitePlan'
        }),
        suspectedOrdnanceAreaUploads: this.createUploadsFormArray(model.uploads, {
          tag: 'suspectedOrdnanceArea',
          validators: [
            ...(config.explosiveField === FieldConfigEnum.REQUIRED
              ? [requireSuspectedOrdnanceAreaDocumentsValidator]
              : [])
          ]
        }),
        areas: [model.areas || []],
        lineChangeComment: [
          model.lineChangeComment || null,
          [
            ...(config.lineChangeCommentField === FieldConfigEnum.REQUIRED
              ? [requireOnLineChangeValidator]
              : [])
          ]
        ]
      },
      {
        validators: [...(model.area === AllAreaEnum.Power ? [atLeastOneInstallationRequiredValidator] : [])]
      }
    );
  }

  private createContactDataForm(
    model: ConnectionChangeDto,
    config: ConnectionChangeRequestTypeConfig
  ): ORContactDataForm {
    return this.formBuilder.group({
      customer: this.createFormsService.createContactDataFormV2(model?.customer || {}, true),
      differingInvoiceAddress: [!!model?.invoiceContact?.salutation],
      invoiceContact: this.createFormsService.createContactDataFormV2(model?.invoiceContact || {}, false)
    });
  }

  private createAdditionalInformationForm(
    model: ConnectionChangeDto,
    config: ConnectionChangeRequestTypeConfig
  ): ORAdditionalInformationForm {
    return this.formBuilder.group({
      isOwner: [!!model?.isOwner],
      mandate: this.createUploadsFormArray(model.uploads, {
        tag: 'mandate',
        validators: [requireMandateDocumentsValidator]
      }),
      ownership: this.createUploadsFormArray(model.uploads, {
        tag: 'ownership',
        validators: [
          ...(config.ownershipVerificationField === FieldConfigEnum.REQUIRED
            ? [requireDocumentsValidator]
            : [])
        ]
      }),
      supplyAppointmentWishDate: [
        model.supplyAppointmentWishDate ? new Date(model.supplyAppointmentWishDate) : null,
        [
          ...(config.supplyAppointmentWishDateField === FieldConfigEnum.REQUIRED &&
          config.connectionChangeRequestType !== ConnectionChangeRequestTypeEnum.TEMPORARY
            ? [Validators.required]
            : [])
        ]
      ]
    });
  }

  private createUploadsFormArray(
    uploads: DocumentDtoModel[],
    options: { tag: string; validators?: ValidatorFn[] }
  ): FormArray<UploadForm> {
    const { tag, validators = [] } = options;
    return this.formBuilder.array(
      uploads
        .filter((document) => document.tag === tag)
        .map((document) => this.createFormsService.createUploadForm(document)),
      validators
    );
  }
}

const requireSuspectedOrdnanceAreaDocumentsValidator: ValidatorFn = (control: FormArray<UploadForm>) => {
  if (!control.parent) return null;
  const { civilEngineering } = (control.parent as ORTechnicalDataForm).controls;
  const requiredEnums = [CivilEngineeringEnum.PUBLIC_ONLY, CivilEngineeringEnum.YES];
  const requireDocuments = !!civilEngineering.value && requiredEnums.includes(civilEngineering.value);
  const isValid = requireDocuments ? !!control.length : true;
  return isValid ? null : { required: true };
};

const requireOnPowerIncreaseValidator: ValidatorFn = (control: FormControl) => {
  if (!control.parent?.parent) return null;
  const powerIncrease = (control.parent.parent as OrderRequestWizardModelForm).controls
    .connectionChangeOperations.controls.powerIncrease;
  const isValid = powerIncrease.value ? !!control.value : true;
  return isValid ? null : { required: true };
};

const requireOnLineChangeValidator: ValidatorFn = (control: FormControl<number>) => {
  if (!control.parent?.parent) return null;
  const moveConnectionLine = (control.parent.parent as OrderRequestWizardModelForm).controls
    .connectionChangeOperations.controls.moveConnectionLine;
  const isValid = moveConnectionLine.value ? !!control.value : true;
  return isValid ? null : { required: true };
};

const minTechnicalRequiredPowerValidator: ValidatorFn = (control: FormControl<number>) => {
  if (!control.parent?.parent) return null;
  const { powerIncrease } = (control.parent.parent as OrderRequestWizardModelForm).controls
    .connectionChangeOperations.controls;
  const { installedPower } = (control.parent as ORTechnicalDataForm).controls;
  if (!control.value || !installedPower.value || !powerIncrease.value) return null;
  const isValid = control.value >= installedPower.value;
  return isValid ? null : { minRequiredPower: true };
};

const atLeastOneInstallationRequiredValidator: ValidatorFn = (group: ORTechnicalDataForm) => {
  if (!group?.parent) return null;
  const { installedPower, installedFuse } = group.controls;
  const { powerIncrease } = (group.parent as OrderRequestWizardModelForm).controls.connectionChangeOperations
    .controls;
  if (!powerIncrease.value) return null;
  const isValid = !!(installedPower?.value || installedFuse?.value);
  return isValid ? null : { atLeastOneInstallationRequired: true };
};

const requireMandateDocumentsValidator: ValidatorFn = (control: FormArray<UploadForm>) => {
  if (!control.parent) return null;
  const { isOwner } = (control.parent as ORAdditionalInformationForm).controls;
  const isValid = !isOwner.value ? !!control.length : true;
  return isValid ? null : { required: true };
};

const requireMeterNumberValidator: ValidatorFn = (control: FormControl<number>) => {
  if (!control.parent) return null;
  const { hasNoMeter } = (control.parent as ORTechnicalDataForm).controls;

  // If "hasNoMeter" is checked or the control has a value, the field is valid
  if (hasNoMeter.value || !!control.value) return null;

  // Only invalid if "hasNoMeter" is not checked and field is empty
  return { required: true };
};
